import React from 'react';
import { useAd } from '../../contexts/AdContext';
import { Settings, ToggleLeft, Clock, TestTube, Zap } from 'lucide-react';

export const AdSettings: React.FC = () => {
  const { adConfig, updateAdConfig } = useAd();

  const handleToggle = (key: keyof typeof adConfig, value: any) => {
    updateAdConfig({ [key]: value });
  };

  return (
    <div className="space-y-6">
      {/* Enable/Disable Ads */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ToggleLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Enable Ads
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Master toggle for all ad functionality
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('enabled', !adConfig.enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              adConfig.enabled ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                adConfig.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Test Mode */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <TestTube className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Test Mode
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Enable for development and testing
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('testMode', !adConfig.testMode)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              adConfig.testMode ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                adConfig.testMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Ad Frequency */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Ad Frequency
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Control how often ads are shown to users
            </p>
          </div>
        </div>
        <div className="space-y-3">
          {(['low', 'medium', 'high'] as const).map((frequency) => (
            <label key={frequency} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="frequency"
                value={frequency}
                checked={adConfig.frequency === frequency}
                onChange={() => handleToggle('frequency', frequency)}
                className="text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {frequency}
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {frequency === 'low' ? '5 minutes between ads' : 
                   frequency === 'medium' ? '3 minutes between ads' : 
                   '1 minute between ads'}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Zone ID Configuration */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <div className="flex items-center space-x-3 mb-4">
          <Zap className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              Propeller Zone ID
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Your PropellerAds zone identifier
            </p>
          </div>
        </div>
        <input
          type="text"
          value={adConfig.propellerZoneId}
          onChange={(e) => handleToggle('propellerZoneId', e.target.value)}
          placeholder="Enter your zone ID"
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Status Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
          Current Status
        </h3>
        <div className="space-y-1">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            {adConfig.enabled 
              ? '✅ Ads are currently enabled and will be shown to users.'
              : '❌ Ads are currently disabled and will not be shown to users.'
            }
          </p>
          {adConfig.testMode && (
            <p className="text-xs text-blue-700 dark:text-blue-300">
              🧪 Test mode is active - real ads will be shown in production.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}; 